import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Phone, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { BRAND, NAV_LINKS } from "@/data/products";
import { useQuote } from "@/context/QuoteContext";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // which mobile nav item is open
  const { totalCount, setIsOpen } = useQuote();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  const testId = (label) => label.toLowerCase().replace(/[/\s]+/g, "-").replace(/[^a-z0-9-]/g, "");

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 bg-[#FAF9F6]/95 backdrop-blur-xl border-b border-[#D4AF37]/20"
    >
      {/* Top contact strip */}
      <div className="bg-[#1A1A1A] text-[#FAF9F6] text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 tracking-wider">
            <Phone className="w-3 h-3 text-[#D4AF37]" />
            <a href={`tel:${BRAND.phone}`} data-testid="top-phone" className="hover:text-[#D4AF37] transition-colors">
              {BRAND.phone}
            </a>
            <span className="hidden md:inline opacity-50 mx-2">•</span>
            <a href={`mailto:${BRAND.email}`} data-testid="top-email" className="hidden md:inline hover:text-[#D4AF37] transition-colors">
              {BRAND.email}
            </a>
          </div>
          <span className="uppercase tracking-[0.25em] text-[10px] text-[#D4AF37]">Factory-Direct since 2023</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center shrink-0">
          <img
            src={BRAND.logo}
            alt="Hospitality Hub"
            className="h-14 sm:h-16 md:h-20 lg:h-[88px] w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden min-[1360px]:flex items-center gap-4 2xl:gap-6">
          {NAV_LINKS.map((l) => {
            const hasDrop = Array.isArray(l.dropdown) && l.dropdown.length > 0;
            if (!hasDrop) {
              if (l.href) {
                return (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-link-${testId(l.label)}`}
                    className="whitespace-nowrap text-xs uppercase tracking-[0.18em] text-[#1A1A1A] hover:text-[#D4AF37] transition-colors"
                  >
                    {l.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${testId(l.label)}`}
                  className={({ isActive }) =>
                    `whitespace-nowrap text-xs uppercase tracking-[0.18em] transition-colors ${
                      isActive ? "text-[#D4AF37]" : "text-[#1A1A1A] hover:text-[#D4AF37]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              );
            }
            // With dropdown
            return (
              <div key={l.to || l.href} className="relative group" data-testid={`nav-group-${testId(l.label)}`}>
                {l.href ? (
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-link-${testId(l.label)}`}
                    className="flex items-center gap-1 whitespace-nowrap text-xs uppercase tracking-[0.18em] text-[#1A1A1A] hover:text-[#D4AF37] transition-colors"
                  >
                    {l.label}
                    <ChevronDown className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" />
                  </a>
                ) : (
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${testId(l.label)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 whitespace-nowrap text-xs uppercase tracking-[0.18em] transition-colors ${
                        isActive ? "text-[#D4AF37]" : "text-[#1A1A1A] hover:text-[#D4AF37]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" />
                  </NavLink>
                )}
                {/* Bridge to prevent hover gap */}
                <div className="absolute left-0 right-0 top-full h-3" aria-hidden />
                <div
                  data-testid={`dropdown-${testId(l.label)}`}
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[280px] bg-white border border-[#D4AF37]/25 shadow-[0_20px_50px_rgb(0,0,0,0.08)] opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300"
                >
                  <div className="py-2">
                    {l.dropdown.map((sub) => (
                      sub.href ? (
                        <a
                          key={sub.href}
                          href={sub.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`dropdown-item-${testId(sub.label)}`}
                          className="block px-6 py-3 text-sm text-[#1A1A1A] hover:text-[#D4AF37] hover:bg-[#FAF9F6] transition-colors border-b border-[#F0EDE4] last:border-b-0 font-serif-display leading-snug"
                        >
                          {sub.label}
                        </a>
                      ) : (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          data-testid={`dropdown-item-${testId(sub.label)}`}
                          className="block px-6 py-3 text-sm text-[#1A1A1A] hover:text-[#D4AF37] hover:bg-[#FAF9F6] transition-colors border-b border-[#F0EDE4] last:border-b-0 font-serif-display leading-snug"
                        >
                          {sub.label}
                        </Link>
                      )
                    ))}
                  </div>
                  {!l.href && (
                    <div className="px-6 py-3 bg-[#FAF9F6] border-t border-[#D4AF37]/15">
                      <Link
                        to={l.to}
                        data-testid={`dropdown-view-all-${testId(l.label)}`}
                        className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#B5952F] inline-flex items-center gap-1"
                      >
                        View all {l.label} →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="open-quote-drawer-btn"
            onClick={() => setIsOpen(true)}
            className="relative inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#D4AF37] text-white px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Quote</span>
            {totalCount > 0 && (
              <span data-testid="quote-count-badge" className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#1A1A1A] text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                {totalCount}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="min-[1360px]:hidden p-2 text-[#1A1A1A]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="min-[1360px]:hidden border-t border-[#D4AF37]/20 bg-[#FAF9F6]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => {
              const hasDrop = Array.isArray(l.dropdown) && l.dropdown.length > 0;
              const expanded = mobileExpanded === l.label;
              if (!hasDrop) {
                if (l.href) {
                  return (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`mobile-nav-${testId(l.label)}`}
                      className="py-3 text-sm uppercase tracking-[0.18em] border-b border-[#E5E5E5] text-[#1A1A1A]"
                    >
                      {l.label}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${testId(l.label)}`}
                    className={({ isActive }) =>
                      `py-3 text-sm uppercase tracking-[0.18em] border-b border-[#E5E5E5] ${isActive ? "text-[#D4AF37]" : "text-[#1A1A1A]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                );
              }
              return (
                <div key={l.to || l.href} className="border-b border-[#E5E5E5]">
                  <button
                    data-testid={`mobile-toggle-${testId(l.label)}`}
                    onClick={() => setMobileExpanded(expanded ? null : l.label)}
                    className="w-full py-3 flex items-center justify-between text-sm uppercase tracking-[0.18em] text-[#1A1A1A]"
                  >
                    <span>{l.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expanded ? "rotate-180 text-[#D4AF37]" : ""}`} />
                  </button>
                  {expanded && (
                    <div data-testid={`mobile-dropdown-${testId(l.label)}`} className="pb-2 pl-3">
                      {!l.href && (
                        <Link
                          to={l.to}
                          data-testid={`mobile-view-all-${testId(l.label)}`}
                          className="block py-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37]"
                        >
                          View all {l.label}
                        </Link>
                      )}
                      {l.dropdown.map((sub) => (
                        sub.href ? (
                          <a
                            key={sub.href}
                            href={sub.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`mobile-dropdown-item-${testId(sub.label)}`}
                            className="block py-2 text-sm text-[#1A1A1A] hover:text-[#D4AF37] font-serif-display"
                          >
                            {sub.label}
                          </a>
                        ) : (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            data-testid={`mobile-dropdown-item-${testId(sub.label)}`}
                            className="block py-2 text-sm text-[#1A1A1A] hover:text-[#D4AF37] font-serif-display"
                          >
                            {sub.label}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
